import React from 'react';

/**
 * Parses a free-text string for "Label: * item1 * item2" patterns and
 * returns a sequence of paragraph + bulleted-list blocks.
 *
 * Display-only — the source string remains untouched, so admins can keep
 * editing recipes with plain stars as separators.
 *
 * Heuristics (in order):
 *   1. Split the input on blank lines / newlines into segments
 *   2. Within each segment, also split before any capitalized `Label: *`
 *      that appears mid-line (catches "... Tekniske noter: * ..." inline)
 *   3. Each segment that matches `^Label: * item * ...$` becomes a list
 *   4. Everything else renders as a normal paragraph
 */
export const parseStarBlocks = (text) => {
  if (!text || typeof text !== 'string') return [];

  // Step 1 — coarse split on line breaks
  const lineSegments = text.split(/\n+/).map((s) => s.trim()).filter(Boolean);

  // Step 2 — for each line, also fragment at every "<Capital>word…: *"
  // that appears AFTER the line's start (so inline labels become their own
  // segment instead of getting swallowed into the previous list's items).
  const inlineLabel = /(?<=[\s.!?])([A-ZÆØÅÄÖÜ][^:*\n]{1,60}:\s*\*\s+)/g;
  const segments = [];
  for (const seg of lineSegments) {
    const positions = [];
    let m;
    inlineLabel.lastIndex = 0;
    while ((m = inlineLabel.exec(seg)) !== null) {
      // m.index points directly at the capital letter (the lookbehind is
      // zero-width and the captured group starts at the capital).
      positions.push(m.index);
    }
    if (positions.length === 0) {
      segments.push(seg);
      continue;
    }
    let cursor = 0;
    for (const pos of positions) {
      const slice = seg.slice(cursor, pos).trim();
      if (slice) segments.push(slice);
      cursor = pos;
    }
    const tail = seg.slice(cursor).trim();
    if (tail) segments.push(tail);
  }

  // Step 3 — classify each segment
  const out = [];
  for (const seg of segments) {
    const m = seg.match(/^([^:*\n]{1,80}):\s*\*\s+([\s\S]+)$/);
    if (m) {
      const items = m[2]
        .split(/\s*\*\s+/)
        .map((s) => s.trim())
        .filter(Boolean);
      if (items.length > 0) {
        out.push({ type: 'list', heading: m[1].trim(), items });
        continue;
      }
    }
    out.push({ type: 'paragraph', text: seg });
  }
  return out;
};

const RichText = ({ text, className = '' }) => {
  const blocks = parseStarBlocks(text);
  if (blocks.length === 0) return null;

  return (
    <div className={className} data-testid="rich-text">
      {blocks.map((b, i) => {
        if (b.type === 'list') {
          return (
            <div key={i} className="mb-3 last:mb-0">
              <div className="font-semibold text-gray-800 mb-1">{b.heading}:</div>
              <ul className="list-disc list-inside space-y-1 text-gray-700">
                {b.items.map((it, j) => (
                  <li key={j}>{it}</li>
                ))}
              </ul>
            </div>
          );
        }
        // Paragraph — preserve single newlines as <br/>
        return (
          <p key={i} className="mb-3 last:mb-0 whitespace-pre-line">
            {b.text}
          </p>
        );
      })}
    </div>
  );
};

export default RichText;
